//src/types/Customer/CustomerInterfaces.ts

import { membershipType, Roles } from '../Helper/Enums'
import { Part141Courses } from '../Part141/Part141Enums'
import { type Address } from '../Helper/Address'
import { CalendarView } from '../Store/StoreEnums'

export interface EmergencyContact extends Address {
  cellphone: string
  email: string
  firstname: string
  middlename: string
  lastname: string
  relationship: string
}

export interface User {
  alias: string
  firstname: string
  middlename: string
  lastname: string
  cellPhone: string
  email: string
  phone: string
  birthday: Date | null
  picture: string
  compliance: UserCompliance
}

export interface Membership {
  memberType: membershipType
  memberStart: Date | null
  memberExpiration: Date | null
  username: string
  userCode: string
  pendingMemberType: membershipType | null
  pendingChangeDate: Date | null
}

export interface Admin {
  roles: Roles[]
  isActive: boolean
  scheduleSuspended: boolean
  accountLock: boolean
  isGrounded: boolean
  renewalAllowed: boolean
  timeClock?: boolean
  closedAt?: Date | null
  closedBy?: string | null
  reopenedAt?: Date | null
  reopenedBy?: string | null
}

export interface Settings {
  defaultCourse?: Part141Courses
  defaultCalendarView?: CalendarView
  ownerQbAccount?: string // QuickBooks account name for this customer, used in billing
}

export interface MailTo extends Address {
  name: string
}

export function createMailTo(init: Partial<MailTo> = {}): MailTo {
  return {
    address: '',
    address2: '',
    city: '',
    country: '',
    state: '',
    zip: '',
    name: '',
    ...init
  }
}

export function createUser(init: Partial<User> = {}): User {
  return {
    alias: '',
    firstname: '',
    middlename: '',
    lastname: '',
    cellPhone: '',
    email: '',
    phone: '',
    birthday: null,
    picture: '',
    compliance: createUserCompliance(),
    ...init
  }
}

export function createSettings(init: Partial<Settings> = {}): Settings {
  return {
    ...init
  }
}

export function createMembership(init: Partial<Membership> = {}): Membership {
  return {
    memberType: membershipType.none,
    memberStart: new Date(),
    memberExpiration: null,
    username: '',
    userCode: '',
    pendingMemberType: null,
    pendingChangeDate: null,
    ...init
  }
}

export function createEmergencyContact(init: Partial<EmergencyContact> = {}): EmergencyContact {
  return {
    address: '',
    address2: '',
    city: '',
    country: '',
    state: '',
    zip: '',
    cellphone: '',
    email: '',
    firstname: '',
    middlename: '',
    lastname: '',
    relationship: '',
    ...init
  }
}

export function createAdmin(init: Partial<Admin> = {}): Admin {
  return {
    roles: [Roles.User],
    isActive: true,
    renewalAllowed: true,
    scheduleSuspended: false,
    accountLock: false,
    isGrounded: false,
    timeClock: false,
    ...init
  }
}

export interface UserCompliance {
  citizenship: string
  tsaAwareness: Date | null
  tsaApproval: Date | null
  visaExp: Date | null
  i20Exp: Date | null
  i94Exp: Date | null
  fpcExp: Date | null
  passportExp: Date | null
  driverLicExp: Date | null
  resCardExp: Date | null
}

export function createUserCompliance(init: Partial<UserCompliance> = {}): UserCompliance {
  return {
    citizenship: '',
    tsaAwareness: null,
    tsaApproval: null,
    visaExp: null,
    i20Exp: null,
    i94Exp: null,
    fpcExp: null,
    passportExp: null,
    driverLicExp: null,
    resCardExp: null,
    ...init
  }
}
